import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Progress,
  Select,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { createToast } from "../../utils/toastUtils";
import { uploadBlobMultipart } from "../../services/audioUploader";
import { getAllOrganizations, getAllUsers } from "../../services/adminService";
import { useAdminData } from "../../hooks/useAdminData";
import { Api } from "../../Api";

const fmtBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const STORAGE_KEY = "ethosphere.userBulkUpload.v1";

const safeJsonParse = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const ConversationLoader = () => {
  const toast = useToast();
  const toastHelper = createToast(toast);

  const stored = useMemo(() => {
    try {
      return safeJsonParse(window?.localStorage?.getItem(STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  }, []);

  const [selectedOrgId, setSelectedOrgId] = useState(stored?.org?.id ? String(stored.org.id) : "");
  const [selectedUserId, setSelectedUserId] = useState(stored?.user?.id ? String(stored.user.id) : "");

  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploaded, setUploaded] = useState(stored?.uploaded || null);
  const [resumeState, setResumeState] = useState(stored?.resumeState || null);
  const [isLoadingBulk, setIsLoadingBulk] = useState(false);
  const [bulkLoadResult, setBulkLoadResult] = useState(stored?.bulkLoadResult || null);

  const inferredType = useMemo(() => file?.type || "application/octet-stream", [file]);

  const fetchOrganizations = useMemo(() => () => getAllOrganizations(), []);
  const fetchUsers = useMemo(() => () => getAllUsers(selectedOrgId ? parseInt(selectedOrgId, 10) : null), [selectedOrgId]);

  const { data: orgData } = useAdminData(fetchOrganizations, []);
  const { data: usersData } = useAdminData(fetchUsers, [selectedOrgId]);

  const organizations = orgData?.organizations || [];
  const users = usersData?.users || [];

  const selectedOrg = useMemo(() => {
    const id = parseInt(selectedOrgId, 10);
    if (!id) return null;
    return organizations.find((o) => o.id === id) || null;
  }, [organizations, selectedOrgId]);

  const selectedUser = useMemo(() => {
    const id = parseInt(selectedUserId, 10);
    if (!id) return null;
    return users.find((u) => u.id === id) || null;
  }, [users, selectedUserId]);

  const persist = (next) => {
    try {
      window?.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    persist({
      org: selectedOrg ? { id: selectedOrg.id, name: selectedOrg.name } : selectedOrgId ? { id: parseInt(selectedOrgId, 10) } : null,
      user: selectedUser ? { id: selectedUser.id, email: selectedUser.email } : selectedUserId ? { id: parseInt(selectedUserId, 10) } : null,
      uploaded,
      resumeState,
      bulkLoadResult,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, selectedUserId, uploaded, resumeState, bulkLoadResult, selectedOrg?.name, selectedUser?.email]);

  const handlePickFile = (e) => {
    const next = e.target.files?.[0] || null;
    setFile(next);
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!file || isUploading || !selectedUserId) return;
    setIsUploading(true);
    setBulkLoadResult(null);
    try {
      const res = await uploadBlobMultipart({
        uploadType: "conversation",
        objectId: null,
        blob: file,
        fileName: file.name,
        setUploadProgress,
        toastFn: (t) => toast(t),
        contentTypeOverride: file.type,
        resumeState,
        onResumeStateChange: (s) => setResumeState(s),
        maxRetriesPerPart: 3,
      });

      if (res.status === "completed") {
        setUploaded({
          upload_uuid: res.uuid,
          bucketName: res.bucketName,
          filePath: res.filePath,
          fileName: file.name,
          size: file.size,
          contentType: inferredType,
          uploadedAt: new Date().toISOString(),
        });
        setResumeState(null);
        toastHelper.success("Upload completed");
      } else if (res.status === "paused") {
        setResumeState(res.resumeState);
        toastHelper.error("Upload paused", res.errorMessage || "Some parts failed. You can retry/resume.");
      } else {
        toastHelper.error("Upload failed", res.errorMessage || "Upload failed.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteLocal = () => {
    setUploaded(null);
    setResumeState(null);
    setBulkLoadResult(null);
    setUploadProgress(0);
    setFile(null);
    try {
      window?.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    toastHelper.success("Cleared local state");
  };

  const BULK_LOAD_ENDPOINT =
    process.env.REACT_APP_BULK_LOAD_ENDPOINT || "/admin/bulk_load";

  const handleBulkLoad = async () => {
    if (!uploaded || !selectedUserId || isLoadingBulk) return;
    setIsLoadingBulk(true);
    try {
      const payload = {
        organization_id: selectedOrgId ? parseInt(selectedOrgId, 10) : undefined,
        user_id: parseInt(selectedUserId, 10),
        bucket_name: uploaded.bucketName,
        file_path: uploaded.filePath,
        upload_uuid: uploaded.upload_uuid,
        file_name: uploaded.fileName,
      };

      const res = await Api.post(BULK_LOAD_ENDPOINT, payload);
      setBulkLoadResult({
        requestedAt: new Date().toISOString(),
        endpoint: BULK_LOAD_ENDPOINT,
        response: res.data,
      });
      toastHelper.success("Load requested");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toastHelper.error(
        "Failed to start load",
        status === 404
          ? `Endpoint not found: ${BULK_LOAD_ENDPOINT}`
          : detail || "Load request failed"
      );
    } finally {
      setIsLoadingBulk(false);
    }
  };

  const uploadSummary = useMemo(() => {
    if (!resumeState) return null;
    const uploadedParts = Array.isArray(resumeState.parts) ? resumeState.parts.length : 0;
    const totalParts = resumeState.num_parts || 0;
    return { uploadedParts, totalParts };
  }, [resumeState]);

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <Box>
          <Text fontSize="sm" fontWeight="semibold">
            User Bulk Upload
          </Text>
          <Text fontSize="xs" color="gray.600" mt={0.5}>
            Select a user, upload a multi-hour audio file to S3 (multipart), then trigger a bulk load.
          </Text>
        </Box>
      </Alert>

      <Box bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200" boxShadow="sm" p={6}>
        <VStack spacing={5} align="stretch">
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
              Upload & Load
            </Text>
            <Badge variant="subtle" borderRadius="full" px={2} colorScheme="blue" fontSize="xs">
              S3 multipart upload
            </Badge>
          </Flex>

          <Divider />

          <HStack spacing={4} align="flex-end" wrap="wrap">
            <FormControl maxW="320px">
              <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                Organization
              </FormLabel>
              <Select
                size="sm"
                value={selectedOrgId}
                onChange={(e) => {
                  setSelectedOrgId(e.target.value);
                  setSelectedUserId("");
                }}
                placeholder="Select organization"
                borderRadius="md"
                bg="white"
              >
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl maxW="360px" isDisabled={!selectedOrgId}>
              <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                User
              </FormLabel>
              <Select
                size="sm"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                placeholder={selectedOrgId ? "Select user" : "Select org first"}
                borderRadius="md"
                bg="white"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email}
                  </option>
                ))}
              </Select>
            </FormControl>
          </HStack>

          {uploaded ? (
            <Box bg="gray.50" borderRadius="lg" borderWidth="1px" borderColor="gray.200" p={5}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    Previously uploaded audio
                  </Text>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    This is stored locally (screen state) and shown read-only.
                  </Text>
                </Box>
                <HStack spacing={2}>
                  <Button size="sm" variant="outline" onClick={handleDeleteLocal}>
                    Delete
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleBulkLoad}
                    isLoading={isLoadingBulk}
                    loadingText="Loading"
                    isDisabled={!selectedUserId}
                  >
                    Load
                  </Button>
                </HStack>
              </Flex>

              <Divider my={4} />

              <VStack align="stretch" spacing={2}>
                <Text fontSize="xs" color="gray.700">
                  Org: <Code fontSize="xs">{selectedOrg?.name || selectedOrgId || "—"}</Code>
                </Text>
                <Text fontSize="xs" color="gray.700">
                  User: <Code fontSize="xs">{selectedUser?.email || selectedUserId || "—"}</Code>
                </Text>
                <Text fontSize="xs" color="gray.700">
                  Bucket: <Code fontSize="xs">{uploaded.bucketName}</Code>
                </Text>
                <Text fontSize="xs" color="gray.700">
                  Path: <Code fontSize="xs">{uploaded.filePath}</Code>
                </Text>
                <Text fontSize="xs" color="gray.500">
                  File: {uploaded.fileName} · {fmtBytes(uploaded.size)}
                </Text>
                {bulkLoadResult && (
                  <Box mt={2}>
                    <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                      Last load request
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Endpoint: <Code fontSize="xs">{bulkLoadResult.endpoint}</Code>
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Requested: <Code fontSize="xs">{bulkLoadResult.requestedAt}</Code>
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          ) : (
            <>
              <FormControl>
            <FormLabel fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
              Audio file
            </FormLabel>
            <Input
              type="file"
              accept=".mp4,.m4a,.webm,.wav,audio/*,video/*"
              onChange={handlePickFile}
              bg="white"
              borderRadius="md"
              p={1}
            />
            <HStack mt={2} spacing={3}>
              <Text fontSize="xs" color="gray.600">
                {file ? file.name : "No file selected"}
              </Text>
              {file && (
                <>
                  <Text fontSize="xs" color="gray.400">
                    {fmtBytes(file.size)}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    {inferredType}
                  </Text>
                </>
              )}
            </HStack>
              </FormControl>

              {resumeState && (
                <Alert status="warning" borderRadius="lg">
                  <AlertIcon />
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      Upload paused
                    </Text>
                    <Text fontSize="xs" color="gray.700" mt={0.5}>
                      {uploadSummary
                        ? `${uploadSummary.uploadedParts} / ${uploadSummary.totalParts} parts uploaded. Re-upload will only send missing parts.`
                        : "Some parts failed. You can resume."}
                    </Text>
                    {!file && (
                      <Text fontSize="xs" color="gray.600" mt={1}>
                        Select the same file to resume.
                      </Text>
                    )}
                  </Box>
                </Alert>
              )}

              <HStack spacing={2}>
                <Button
                  colorScheme="blue"
                  borderRadius="md"
                  onClick={handleUpload}
                  isDisabled={!file || !selectedUserId}
                  isLoading={isUploading}
                  loadingText={resumeState ? "Resuming" : "Uploading"}
                >
                  {resumeState ? "Resume upload" : "Upload to S3"}
                </Button>
                <Button
                  variant="outline"
                  borderRadius="md"
                  onClick={() => {
                    setResumeState(null);
                    setUploadProgress(0);
                    toastHelper.success("Restart ready");
                  }}
                  isDisabled={!resumeState || isUploading}
                >
                  Restart
                </Button>
              </HStack>

              <Progress
                value={uploadProgress}
                size="sm"
                borderRadius="full"
                colorScheme="blue"
                bg="gray.100"
              />
            </>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};

export default ConversationLoader;

